import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import images from "../../../images";
import TransactionItem from "./TransactionItem";
import { useGetWithdrawHistoryQuery } from "../../../services/apiSlice";
import { useTranslation } from "react-i18next";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import NewHeader from "../../Home/NewHeader";

const HistoryWithdraw = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const observerRef = useRef(null);
  const loadingRef = useRef(null);

  // State cho phân trang
  const [page, setPage] = useState(1);
  const [transactions, setTransactions] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 10;
  const {
    data: withdrawHistory,
    isLoading,
    isFetching,
    error,
  } = useGetWithdrawHistoryQuery({
    page: page,
    limit: PAGE_SIZE,
    order: "DESC",
  });

  useEffect(() => {
    setPage(1);
    setTransactions([]);
    setHasMore(true);
  }, []);

  useEffect(() => {
    if (withdrawHistory?.data) {
      if (page === 1) {
        setTransactions(withdrawHistory.data);
      } else {
        setTransactions((prev) => [...prev, ...withdrawHistory.data]);
      }

      // setHasMore(withdrawHistory.data.length === PAGE_SIZE);
      setHasMore(withdrawHistory.data.length >= PAGE_SIZE);
    }
  }, [withdrawHistory, page]);

  const lastTransactionElementRef = useCallback(
    (node) => {
      if (isLoading || isFetching) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            setPage((prevPage) => prevPage + 1);
          }
        },
        {
          rootMargin: "100px",
        }
      );

      if (node) observerRef.current.observe(node);
    },
    [isLoading, isFetching, hasMore]
  );
  const uniqueTransactions = useMemo(() => {
    const seen = new Set();
    return transactions.filter((item) => {
      if (seen.has(item.withdraw_id)) {
        return false;
      }
      seen.add(item.withdraw_id);
      return true;
    });
  }, [transactions]);

  if (isLoading && page === 1) {
    return (
      <div className="flex justify-center items-center h-screen">
        {t("base.loading")}
      </div>
    );
  }

  if (error && page === 1) {
    return (
      <div className="flex justify-center items-center h-screen">
        {t("base.errorLoadingData")}
      </div>
    );
  }

  const topContent = (
    <div className="flex flex-col  gap-4">
      <div onClick={() => navigate("/profile")} className="cursor-pointer">
        <img
          src={images.ArrowLeftIconGray}
          alt="Withdraw"
          className="w-6 h-6"
        />
      </div>
      <h1 className="text-2xl font-bold text-[#253FF6]">
        {t("historyWithdraw.withdrawHistory")}
      </h1>
    </div>
  );

  return (
    <>
      <LoadingCommon statusLoading={isFetching || isLoading} />
      <div className="flex flex-col w-full max-w-[480px] bg-white ">
        <div className="fixed top-0 left-0 w-full z-50">
          <NewHeader />
        </div>
        <div className="h-[30px]"></div>
        <div className="flex flex-col flex-1 gap-3 py-3.5 px-[30px] mt-10">
          {topContent}
        </div>
        <section className="flex relative flex-col gap-4 items-start px-[30px] mt-4">
          {uniqueTransactions.length === 0 && !isLoading && !isFetching ? (
            <div className="flex justify-center items-center p-8">
              <p>{t("historyWithdraw.noWithdrawHistory")}</p>
            </div>
          ) : (
            <>
              {uniqueTransactions.map((item, index) => {
                const isLastElement = uniqueTransactions.length === index + 1;
                const uniqueKey = `${item.withdraw_id}-${index}`;
                return (
                  <TransactionItem
                    key={uniqueKey}
                    ref={isLastElement ? lastTransactionElementRef : null}
                    status={
                      item.status === "APPROVED"
                        ? "completed"
                        : item.status === "REJECTED"
                        ? "unsuccessful"
                        : "pending"
                    }
                    createdAt={item.created_at}
                    transactionsFee={item.transaction_fee}
                    receiveAmount={item.receive_amount}
                    tokenAmount={item.token_amount}
                    bankAccount={item.bank_account}
                  />
                );
              })}
              {(isFetching || (isLoading && page > 1)) && (
                <div
                  ref={loadingRef}
                  className="flex justify-center items-center py-4"
                >
                  <div className="w-6 h-6 border-2 border-[#253FF6] border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-2 text-sm text-gray-600">
                    {t("base.loading")}
                  </span>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
};

export default HistoryWithdraw;
